import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  StatusBar,
  TouchableOpacity,
  SafeAreaView,
  Platform,
  TextInput,
  Alert,
  Keyboard,
  TouchableWithoutFeedback,
} from 'react-native';
import { Camera } from 'react-native-camera-kit';
import { COLORS } from '../theme/themeTokens';
import { COLORS_WRONG } from '../theme/colorsWrong';
import { parseScannedAddress } from '../utils/scannedAddress';

interface QRScannerScreenProps {
  onScan: (address: string) => void;
  onClose: () => void;
}

const QRScannerScreen: React.FC<QRScannerScreenProps> = ({
  onScan,
  onClose,
}) => {
  const [scanned, setScanned] = useState(false);
  const [cameraError, setCameraError] = useState(false);
  const [manualAddress, setManualAddress] = useState('');

  const handleBarCodeRead = (event: { nativeEvent: { codeStringValue: string } }) => {
    if (scanned) return;
    
    const scannedValue = event.nativeEvent.codeStringValue;
    
    if (scannedValue) {
      setScanned(true);
      
      // A bare address, or a payment link around one (utils/scannedAddress).
      const address = parseScannedAddress(scannedValue);
      
      onScan(address);
    }
  };

  const handleManualSubmit = () => {
    if (manualAddress.trim().length > 0) {
      onScan(manualAddress.trim());
    }
  };

  // Fallback for simulator or camera errors
  if (cameraError) {
    return (
      <TouchableWithoutFeedback onPress={Keyboard.dismiss} accessible={false}>
        <View style={styles.fallbackContainer}>
          <StatusBar barStyle="dark-content" backgroundColor={COLORS.pureLight} />
          <SafeAreaView style={styles.fallbackContent}>
            <View style={styles.fallbackHeader}>
              <TouchableOpacity style={styles.closeButtonFallback} onPress={onClose}>
                <Text style={styles.closeButtonTextFallback}>✕</Text>
              </TouchableOpacity>
              <Text style={styles.fallbackTitle}>Enter Address</Text>
              <View style={styles.placeholder} />
            </View>
            
            <View style={styles.fallbackBody}>
              <Text style={styles.fallbackInfo}>
                Camera is not available.{'\n'}Please enter the address manually:
              </Text>
              
              <TextInput
                style={styles.manualInput}
                placeholder="Paste wallet address here"
                placeholderTextColor={COLORS.blueSteel}
                value={manualAddress}
                onChangeText={setManualAddress}
                autoCapitalize="none"
                autoCorrect={false}
                multiline
              />
              
              <TouchableOpacity 
                style={[styles.submitButton, !manualAddress.trim() && styles.submitButtonDisabled]}
                onPress={handleManualSubmit}
                disabled={!manualAddress.trim()}
              >
                <Text style={styles.submitButtonText}>Use Address</Text>
              </TouchableOpacity>
            </View>
          </SafeAreaView>
        </View>
      </TouchableWithoutFeedback>
    );
  }

  return (
    <View style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor={COLORS_WRONG.black} />
      
      <Camera
        style={styles.camera}
        scanBarcode={true}
        onReadCode={handleBarCodeRead}
        showFrame={true}
        laserColor={COLORS.brandOrange}
        frameColor={COLORS.white}
        onError={() => setCameraError(true)}
      />
      
      {/* Overlay with instructions */}
      <SafeAreaView style={styles.overlay}>
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity style={styles.closeButton} onPress={onClose}>
            <Text style={styles.closeButtonText}>✕</Text>
          </TouchableOpacity>
          <Text style={styles.title}>Scan QR Code</Text>
          <View style={styles.placeholder} />
        </View>
        
        {/* Scan frame area - empty to show camera */}
        <View style={styles.scanArea} />
        
        {/* Instructions */}
        <View style={styles.instructionsContainer}>
          <Text style={styles.instructions}>
            Point your camera at a wallet address QR code
          </Text>
        </View>
      </SafeAreaView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS_WRONG.black,
  },
  camera: {
    flex: 1,
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'space-between',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingTop: 10,
    paddingBottom: 20,
    backgroundColor: COLORS.overlay,
  },
  closeButton: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeButtonText: {
    fontSize: 26,
    color: COLORS.white,
    fontWeight: '300',
  },
  title: {
    fontFamily: 'Inter',
    fontWeight: '600',
    fontSize: 20,
    color: COLORS.white,
  },
  placeholder: {
    width: 40,
  },
  scanArea: {
    flex: 1,
  },
  instructionsContainer: {
    paddingHorizontal: 40,
    paddingVertical: 40,
    backgroundColor: COLORS.overlay,
    alignItems: 'center',
    gap: 16,
  },
  instructions: {
    fontFamily: 'Inter',
    fontWeight: '400',
    fontSize: 18,
    color: COLORS.white,
    textAlign: 'center',
    lineHeight: 26,
  },
  // Fallback styles
  fallbackContainer: {
    flex: 1,
    backgroundColor: COLORS.pureLight,
  },
  fallbackContent: {
    flex: 1,
  },
  fallbackHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: COLORS.white,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.blueSteel,
  },
  closeButtonFallback: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeButtonTextFallback: {
    fontSize: 26,
    color: COLORS.steelNavy,
    fontWeight: '300',
  },
  fallbackTitle: {
    fontFamily: 'Inter',
    fontWeight: '600',
    fontSize: 20,
    color: COLORS.steelNavy,
  },
  fallbackBody: {
    flex: 1,
    padding: 20,
    gap: 20,
  },
  fallbackInfo: {
    fontFamily: 'Inter',
    fontWeight: '400',
    fontSize: 18,
    color: COLORS.blueSteel,
    textAlign: 'center',
    lineHeight: 26,
  },
  manualInput: {
    backgroundColor: COLORS.white,
    borderRadius: 12,
    padding: 16,
    fontFamily: 'Inter',
    fontSize: 16,
    color: COLORS.steelNavy,
    minHeight: 100,
    textAlignVertical: 'top',
  },
  submitButton: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 10,
    height: 48,
    backgroundColor: COLORS.brandOrange,
    borderRadius: 4,
  },
  submitButtonDisabled: {
    backgroundColor: COLORS.blueSteel,
  },
  submitButtonText: {
    lineHeight: 22,
    fontFamily: 'Inter',
    fontWeight: '500',
    fontSize: 16,
    color: COLORS.white,
  },
});

export default QRScannerScreen;
